import React, { useState, useRef, useEffect, useMemo } from 'react'
import { sidebarItems } from '../config/sidebarItems'
import { useLocation, Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { filterMenuByPermissions } from '../utils/menuFilter'

export default function MiniSidebar({ showMiniSidebar }) {
  const location = useLocation();
  const [hoveredItem, setHoveredItem] = useState(null);
  const [expandedChildren, setExpandedChildren] = useState({}); // Track which 2nd level items are expanded
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const timeoutRef = useRef(null);
  const dropdownRefs = useRef({});
  const sidebarScrollRef = useRef(null);
  const lastHoveredRef = useRef(null);
  const dropdownContentRef = useRef(null);
  const { hasPermission, isSuperAdmin, permissions } = useAuth();

  // Filter menu items based on permissions - memoized for performance
  const filteredMenuItems = useMemo(() => {
    return filterMenuByPermissions(sidebarItems, hasPermission, isSuperAdmin);
  }, [hasPermission, isSuperAdmin, permissions]);

  // Reset dropdown state when sidebar is toggled
  useEffect(() => {
    // Close any open dropdowns when switching to/from mini sidebar
    setHoveredItem(null);
    setExpandedChildren({});
    lastHoveredRef.current = null;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, [showMiniSidebar]);

  // Detect touch device
  useEffect(() => {
    const checkTouch = () => {
      setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
    };
    checkTouch();
  }, []);

  const isItemActive = (item) => {
    if (item.path && location.pathname.includes(item.path)) {
      return true;
    }
    
    if (item.children) {
      return item.children.some(child => {
        if (child.path && location.pathname.includes(child.path)) {
          return true;
        }
        // Check nested children (3rd level)
        if (child.children) {
          return child.children.some(nestedChild => 
            nestedChild.path && location.pathname.includes(nestedChild.path)
          );
        }
        return false;
      });
    }
    
    return false;
  };

  const handleMouseEnter = (index) => {
    // Only handle hover on non-touch devices
    if (isTouchDevice) return;
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    lastHoveredRef.current = index;
    setHoveredItem(index);
  };

  // Handle mouse move - helps re-open dropdown if mouse was already hovering after scroll
  const handleMouseMove = (index) => {
    // Only handle on non-touch devices and if currently closed but was recently interacted with
    if (isTouchDevice) return;
    
    // If hoveredItem is null but mouse is over an item with children, open it
    if (hoveredItem === null && lastHoveredRef.current === index) {
      setHoveredItem(index);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    }
  };

  const handleMouseLeave = () => {
    // Only handle hover on non-touch devices
    if (isTouchDevice) return;
    
    lastHoveredRef.current = null;
    timeoutRef.current = setTimeout(() => {
      setHoveredItem(null);
      setExpandedChildren({});
    }, 150);
  };

  const handleDropdownMouseEnter = () => {
    // Only handle hover on non-touch devices
    if (isTouchDevice) return;
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  // Touch/tap handler for mobile - toggles dropdown
  const handleItemTap = (index, hasChildren, e) => {
    if (!isTouchDevice || !hasChildren) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    // Toggle: if already open, close it; if closed, open it
    setHoveredItem(prev => prev === index ? null : index);
    
    // Reset expanded children when closing
    if (hoveredItem === index) {
      setExpandedChildren({});
    }
  };

  // Close dropdown when clicking outside (mobile)
  const handleBackdropClick = () => {
    setHoveredItem(null);
    setExpandedChildren({});
  };

  // Close dropdown when navigating (mobile)
  const handleLinkClick = () => {
    if (isTouchDevice) {
      setHoveredItem(null);
      setExpandedChildren({});
    }
  };

  const toggleChildExpansion = (parentIndex, childIndex, e) => {
    e.preventDefault();
    e.stopPropagation();
    const key = `${parentIndex}-${childIndex}`;
    setExpandedChildren(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  /**
   * Dynamic dropdown positioning: aligns dropdown and diamond pointer with parent item.
   * Calculates position so dropdown appears connected and diamond points to parent center.
   */
  const getDropdownStyle = (itemRef) => {
    if (!itemRef) {
      return { top: 0, maxHeight: '70vh', orientation: 'down', diamondOffset: 24 };
    }
    
    const itemRect = itemRef.getBoundingClientRect();
    const viewportPadding = 16;
    const gapFromParent = 8; // Small gap to make dropdown feel connected but not overlapping
    
    const spaceAbove = Math.max(0, itemRect.top - viewportPadding);
    const spaceBelow = Math.max(0, window.innerHeight - itemRect.bottom - viewportPadding);
    
    const orientation = spaceAbove > spaceBelow ? 'up' : 'down';
    
    if (orientation === 'up') {
      // Open upward: dropdown bottom aligns near item bottom
      const dropdownBottom = window.innerHeight - itemRect.bottom - gapFromParent;
      const maxHeight = spaceAbove + itemRect.height + gapFromParent;
      // Diamond offset from dropdown bottom to point at parent item center
      const diamondFromBottom = gapFromParent + (itemRect.height / 2);
      
      return {
        bottom: `${dropdownBottom}px`,
        maxHeight: `${maxHeight}px`,
        orientation: 'up',
        diamondOffset: diamondFromBottom
      };
    } else {
      // Open downward: dropdown top aligns near item top
      const dropdownTop = itemRect.top - gapFromParent;
      const maxHeight = spaceBelow + itemRect.height + gapFromParent;
      // Diamond offset from dropdown top to point at parent item center
      const diamondFromTop = gapFromParent + (itemRect.height / 2);
      
      return {
        top: `${dropdownTop}px`,
        maxHeight: `${maxHeight}px`,
        orientation: 'down',
        diamondOffset: diamondFromTop
      };
    }
  };


  // Close dropdown when clicking outside (anywhere in the app)
  useEffect(() => {
    if (hoveredItem === null) return;

    const handleClickOutside = (event) => {
      const dropdownElement = dropdownContentRef.current;
      const parentItemElement = dropdownRefs.current[hoveredItem];
      
      // Check if click is outside both the dropdown and the parent menu item
      const isClickOutside = 
        dropdownElement && !dropdownElement.contains(event.target) &&
        parentItemElement && !parentItemElement.contains(event.target);

      if (isClickOutside) {
        setHoveredItem(null);
        setExpandedChildren({});
        lastHoveredRef.current = null;
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      }
    };

    // Add listener with slight delay to avoid immediate closure on open
    setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }, 0);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [hoveredItem]);

  // Close dropdown when sidebar is scrolled
  useEffect(() => {
    const sidebarElement = sidebarScrollRef.current;
    if (!sidebarElement) return;

    const handleScroll = () => {
      // Clear any pending timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      
      // Close dropdown and reset state
      setHoveredItem(null);
      setExpandedChildren({});
    };

    sidebarElement.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      sidebarElement.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <div ref={sidebarScrollRef} className='w-24 h-full overflow-y-auto hidden md:block'>
        <div className="flex flex-col items-center pt-6 pb-4">
        {/* Navigation items */}
        <div className="flex flex-col items-center space-y-6 w-full px-2">
          {filteredMenuItems.map((item, index) => {
            const isActive = isItemActive(item);            
            return (
              <div 
                key={index} 
                className="relative w-full"
                onMouseEnter={() => handleMouseEnter(index)}
                onMouseMove={() => handleMouseMove(index)}
                onMouseLeave={handleMouseLeave}
                ref={el => dropdownRefs.current[index] = el}
              >
                {/* Main item */}
                {item.path ? (
                  <Link to={item.path} className="flex flex-col items-center w-full group">
                    <div className={`flex items-center justify-center w-14 h-14 ${isActive ? 'bg-primary text-white' : 'text-theme-text-secondary group-hover:text-primary group-hover:bg-theme-bg-secondary'} rounded-xl transition-all duration-200 mx-auto`}>
                      <item.icon className="w-6 h-6" />
                    </div>
                    <span className={`text-xs mt-1 text-center ${isActive ? 'text-primary font-medium' : 'text-theme-text-secondary group-hover:text-primary'} break-words w-full transition-colors duration-200`}>
                      {item.label}
                    </span>
                  </Link>
                ) : (
                  <div 
                    className="flex flex-col items-center cursor-pointer w-full group"
                    onClick={(e) => handleItemTap(index, item.children && item.children.length > 0, e)}
                  >
                    <div className={`flex items-center justify-center w-14 h-14 ${isActive ? 'bg-primary text-white' : 'text-theme-text-secondary group-hover:text-primary group-hover:bg-theme-bg-secondary'} rounded-xl transition-all duration-200 mx-auto`}>
                      <item.icon className="w-6 h-6" />
                    </div>
                    <span className={`text-xs mt-1 text-center ${isActive ? 'text-primary font-medium' : 'text-theme-text-secondary group-hover:text-primary'} break-words w-full transition-colors duration-200`}>
                      {item.label}
                    </span>
                  </div>
                )}

                {/* Dropdown for items with children */}
                {item.children && hoveredItem === index && (() => {
                  const itemRef = dropdownRefs.current[index];
                  const style = getDropdownStyle(itemRef);
                  
                  return (
                    <div 
                      ref={dropdownContentRef}
                      className="fixed bg-theme-bg-secondary shadow-card rounded-lg z-[100] border border-theme-border-secondary min-w-60 whitespace-nowrap flex flex-col"
                      style={{ 
                        left: '98px', // 96px (sidebar width) + 2px gap
                        ...(style.orientation === 'up' ? { bottom: style.bottom } : { top: style.top }),
                        maxHeight: style.maxHeight
                      }}
                      onMouseEnter={handleDropdownMouseEnter}
                      onMouseLeave={handleMouseLeave}
                    >
                      {/* Triangle indicator - dynamically positioned to point at parent item center */}
                      {style.orientation === 'up' ? (
                        <div 
                          className="absolute -left-1 z-10" 
                          style={{ bottom: `${style.diamondOffset}px` }}
                        >
                          <div className="w-2 h-2 bg-theme-bg-secondary rotate-45 border-l border-b border-theme-border-secondary"></div>
                        </div>
                      ) : (
                        <div 
                          className="absolute -left-1 z-10" 
                          style={{ top: `${style.diamondOffset}px` }}
                        >
                          <div className="w-2 h-2 bg-theme-bg-secondary rotate-45 border-l border-b border-theme-border-secondary"></div>
                        </div>
                      )}
                      
                      {/* Header - fixed */}
                      <div className="flex-shrink-0 px-3 pt-3 pb-1">
                        <h3 className="text-theme-text-primary font-medium text-sm">{item.label}</h3>
                        <div className="h-px bg-theme-border-secondary mt-1 mb-1"></div>
                      </div>
                      
                      {/* Content area - auto height, scrolls only if exceeds available space */}
                      <div className="flex flex-col space-y-1 px-3 pb-3 overflow-y-auto overflow-x-hidden min-h-0" style={{ 
                        scrollbarWidth: 'thin',
                        scrollbarColor: '#E3E3E3 transparent'
                      }}>
                        {item.children.map((child, childIndex) => {
                        const hasNestedChildren = child.children && child.children.length > 0;
                        const isChildActive = child.path && location.pathname.includes(child.path);
                        const isNestedActive = hasNestedChildren && child.children.some(nested => 
                          nested.path && location.pathname.includes(nested.path)
                        );
                        const isActive = isChildActive || isNestedActive;
                        const expansionKey = `${index}-${childIndex}`;
                        const isExpanded = expandedChildren[expansionKey];

                        return (
                          <div key={childIndex} className="relative">
                            {hasNestedChildren ? (
                              <button
                                onClick={(e) => toggleChildExpansion(index, childIndex, e)}
                                className={`w-full flex items-center justify-between px-3 py-2.5 rounded-md text-sm group/child hover:bg-theme-bg-tertiary transition-colors ${
                                  isActive ? 'text-primary font-medium' : 'text-theme-text-secondary'
                                }`}
                              >
                                <div className="flex items-center flex-1 min-w-0">
                                  {child.icon && (
                                    <child.icon className={`w-4 h-4 min-w-4 mr-3 flex-shrink-0 ${
                                      isActive ? 'text-primary' : 'text-theme-text-secondary group-hover/child:text-primary'
                                    }`} />
                                  )}
                                  <span className={`truncate ${
                                    isActive ? 'text-primary font-medium' : 'text-theme-text-secondary group-hover/child:text-primary'
                                  }`}>
                                    {child.label}
                                  </span>
                                </div>
                                <span className={`ml-2 text-theme-text-secondary transition-transform flex-shrink-0 ${
                                  isExpanded ? 'rotate-90' : ''
                                }`}>
                                  ›
                                </span>
                              </button>
                            ) : (
                              <Link 
                                to={child.path || '#'}
                                onClick={handleLinkClick}
                                className={`flex items-center px-3 py-2.5 rounded-md text-sm group/child hover:bg-theme-bg-tertiary transition-colors`}
                              >
                                {child.icon && (
                                  <child.icon className={`w-4 h-4 min-w-4 mr-3 ${
                                    isChildActive ? 'text-primary' : 'text-theme-text-secondary group-hover/child:text-primary'
                                  }`} />
                                )}
                                <span className={`truncate ${
                                  isChildActive ? 'text-primary font-medium' : 'text-theme-text-secondary group-hover/child:text-primary'
                                }`}>
                                  {child.label}
                                </span>
                              </Link>
                            )}

                            {/* Inline expanded 3rd level children */}
                            {hasNestedChildren && isExpanded && (
                              <div className="mt-1 ml-4 pl-4 border-l-2 border-theme-border-secondary space-y-0.5">
                                {child.children.map((nestedChild, nestedIndex) => {
                                  const isNestedChildActive = nestedChild.path && location.pathname.includes(nestedChild.path);
                                  return (
                                    <Link 
                                      key={nestedIndex}
                                      to={nestedChild.path || '#'}
                                      onClick={handleLinkClick}
                                      className={`flex items-center px-3 py-2 rounded-md text-sm group/nested hover:bg-theme-bg-tertiary transition-colors ${
                                        isNestedChildActive ? 'text-primary font-medium bg-primary-muted' : 'text-theme-text-secondary'
                                      }`}
                                    >
                                      {nestedChild.icon && (
                                        <nestedChild.icon className={`w-3.5 h-3.5 min-w-3.5 mr-2.5 ${
                                          isNestedChildActive ? 'text-primary' : 'text-theme-text-secondary group-hover/nested:text-primary'
                                        }`} />
                                      )}
                                      <span className={`truncate ${
                                        isNestedChildActive ? 'text-primary font-medium' : 'text-theme-text-secondary group-hover/nested:text-primary'
                                      }`}>
                                        {nestedChild.label}
                                      </span>
                                    </Link>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                      </div>
                    </div>
                  );
                })()}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  )
}
